import type { ExtractedFieldSource, OcrResult } from '../types';

export interface FieldNarrowing {
  /** Keep only fields of these provenances. A missing `source` is `template`. */
  sources?: readonly ExtractedFieldSource[];
  /** Keep only fields with these keys (raw `key`, not folded). */
  keys?: readonly string[];
}

/**
 * Narrow every result's `extracted_data` to the fields a surface shows.
 *
 * Applied at the ROOT of `OcrResultsPage` (see its `fieldSources` /
 * `fieldKeys` props), so page sections, counters, panel search and the
 * viewer's highlight overlays all narrow together. Results are never dropped
 * — an emptied one still carries the page images the viewer renders.
 *
 * Returns the input untouched when nothing narrows, so a caller passing no
 * filter keeps referential identity for its memo.
 */
export function narrowResultFields(
  results: OcrResult[],
  { sources, keys }: FieldNarrowing
): OcrResult[] {
  const wantedSources = sources ? new Set(sources) : null;
  const wantedKeys = keys && keys.length > 0 ? new Set(keys) : null;
  if (!wantedSources && !wantedKeys) return results;

  return results.map((result) => ({
    ...result,
    extracted_data: (result.extracted_data ?? []).filter(
      (field) =>
        (!wantedSources || wantedSources.has(field.source ?? 'template')) &&
        (!wantedKeys || wantedKeys.has(field.key))
    )
  }));
}
